import React, { useMemo, useState } from "react";
import { Button } from "../../../components/library/button";
import { Drawer } from "../../../components/library/drawer";
import Table, { TableColumns } from "../../../components/table";
import {
  AdvanceSalaryRepaymentStatus,
  AdvanceSalaryStatus,
} from "../../../dto";
import { AdvanceSalaryDetails } from "../../../models";
import EmiDetailsTable from "./emiDetailsTable";
import {
  calculateAdvanceSalarySum,
  calculateRepaymentSumByStatus,
  formatInterestRateType,
} from "./utils";

const COLUMNS_FOR_ADVANCE_SALARY_TABLE: TableColumns[] = [
  { label: "STU ID", field: "_id" },
  { label: "Loan ID", field: "loanId" },
  { label: "Interest Rate", field: "irpa" },
  { label: "Interest Rate Type", field: "interestRateType" },
  { label: "Employee ID", field: "employerEmployeeId" },
  { label: "Total Payable", field: "totalPayable" },
  { label: "Total Paid", field: "totalPaid" },
  { label: "Status", field: "status" },
  // { label: "View EMI Details", field: "emiDetails" },
];

const getTableDataForAdvanceSalary = (context: {
  advanceSalaryList: AdvanceSalaryDetails;
  onViewEmiDetails: (loanId: string) => void;
}) => {
  const { advanceSalaryList, onViewEmiDetails } = context;

  return advanceSalaryList.map((advanceSalary) => {
    const {
      loanId,
      employeeId,
      repaymentDetails,
      status,
      employerEmployeeId,
      _id,
      irpa,
      interestRateType,
    } = advanceSalary;
    return {
      _id,
      loanId,
      employeeId,
      employerEmployeeId,
      totalPayable: calculateAdvanceSalarySum(advanceSalary).toFixed(2),
      totalPaid: calculateRepaymentSumByStatus(
        repaymentDetails,
        AdvanceSalaryRepaymentStatus.PAID,
      ).toFixed(2),
      irpa,
      interestRateType: formatInterestRateType(interestRateType),
      status,
      emiDetails: (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onViewEmiDetails(loanId)}
          children="View EMI Details"
        />
      ),
    };
  });
};

interface AdvanceSalaryTableProps {
  selectedStatus: AdvanceSalaryStatus;
  advanceSalaries: AdvanceSalaryDetails;
}

export default function AdvanceSalaryTable({
  selectedStatus,
  advanceSalaries,
}: AdvanceSalaryTableProps) {
  const [isEmiDetailsDrawerOpen, setIsEmiDetailsDrawerOpen] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

  const handleViewEmiDetails = (loanId: string) => {
    setSelectedLoanId(loanId);
    setIsEmiDetailsDrawerOpen(true);
  };

  const tableDataForAdvanceSalary = useMemo(() => {
    return getTableDataForAdvanceSalary({
      advanceSalaryList: advanceSalaries,
      onViewEmiDetails: handleViewEmiDetails,
    });
  }, [advanceSalaries]);

  const columns = useMemo(() => {
    if (selectedStatus === AdvanceSalaryStatus.CLOSED)
      return COLUMNS_FOR_ADVANCE_SALARY_TABLE.filter(
        (row) => row.field !== "emiDetails",
      );
    return COLUMNS_FOR_ADVANCE_SALARY_TABLE;
  }, [selectedStatus]);

  return (
    <div className="dashboard-container">
      <Table columns={columns} data={tableDataForAdvanceSalary} />

      <Drawer
        open={isEmiDetailsDrawerOpen}
        onClose={() => setIsEmiDetailsDrawerOpen(false)}
        padding="8px"
        stickPosition="right"
        withCloseButton
        size="800px"
        borderRadius="16px"
      >
        {selectedLoanId && (
          <EmiDetailsTable
            loanId={selectedLoanId}
            advanceSalaries={advanceSalaries}
          />
        )}
      </Drawer>
    </div>
  );
}
